import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { applyCallRecordTags, removeCallRecordTags } from '@frontend/api-phone-calls';
import { useAppScopeStore } from '@frontend/scope';
import { RecentCallRecords } from '../types';

// This function is used to optimistically update the call record data in call-history list when a tag is applied
export const optimisticApplyTag = ({
  oldData,
  tag,
  channelId,
}: {
  oldData: InfiniteData<RecentCallRecords> | undefined;
  tag: Tag;
  channelId: string;
}) => {
  if (oldData) {
    return {
      pageParams: oldData.pageParams,
      pages: oldData.pages.map((page) => {
        return {
          ...page,
          data: page.data.map((row) => {
            if (row.channelId === channelId) {
              return {
                ...row,
                tags: [...row.tags, tag],
                tagIds: [...(row.tagIds ?? []), tag.id],
              };
            }
            return row;
          }),
        };
      }),
    };
  }
  return undefined;
};

// This function is used to optimistically update the call record data in call-history list when a tag is removed
export const optimisticRemoveTag = ({
  oldData,
  tag,
  channelId,
}: {
  oldData?: InfiniteData<RecentCallRecords>;
  tag: Tag;
  channelId: string;
}) => {
  if (oldData) {
    return {
      pageParams: oldData.pageParams,
      pages: oldData?.pages.map((page) => {
        return {
          ...page,
          data: page.data.map((row) => {
            if (row.channelId === channelId) {
              return {
                ...row,
                tags: row.tags.filter((tg) => tg.id !== tag.id),
                tagIds: row?.tagIds?.filter((id) => id !== tag.id),
              };
            }
            return row;
          }),
        };
      }),
    };
  }
  return undefined;
};

export const useRecentCallsTagMutation = ({
  queryKey,
  channelId,
  callStartedAt,
  callRecordLocationId,
  optimisticApplyTag,
  optimisticRemoveTag,
  onSuccess,
  onFailure,
}: {
  queryKey: string[];
  channelId: string;
  callStartedAt: string;
  callRecordLocationId: string;
  optimisticApplyTag?: (args: { oldData: any | undefined; tag: Tag; channelId: string }) => any;
  optimisticRemoveTag?: (args: { oldData: any | undefined; tag: Tag; channelId: string }) => any;
  onSuccess?: (args: { tag: Tag; op: 'remove' | 'add'; updatedTagIds?: string[] }) => void;
  onFailure?: (args: { tag: Tag; op: 'remove' | 'add'; updatedTagIds?: string[] }) => void;
}) => {
  const { selectedLocationIds } = useAppScopeStore();

  const qc = useQueryClient();

  const getUpdatedTagIds = () => {
    const data = qc.getQueryData<InfiniteData<RecentCallRecords> | undefined>([selectedLocationIds, ...queryKey]);
    if (!data) return [];
    const allRows = data.pages.flatMap((page) => page.data);
    const row = allRows.find((row) => row.channelId === channelId);
    return row ? row.tags.map((t) => t.id) : [];
  };

  const { mutate: applyTag } = useMutation({
    mutationFn: async (tag: Tag) => {
      return applyCallRecordTags({
        channelId,
        tagIds: [tag.id],
        callRecordLocationId,
        startTime: callStartedAt,
      });
    },
    onMutate: async (tag: Tag) => {
      const oldData = qc.getQueryData<InfiniteData<RecentCallRecords> | undefined>([selectedLocationIds, ...queryKey]);
      qc.setQueryData<InfiniteData<RecentCallRecords> | undefined>([selectedLocationIds, ...queryKey], (oldData) => {
        return optimisticApplyTag?.({ oldData, tag, channelId }) ?? oldData;
      });
      return { oldData };
    },
    onError: (_err, tag, context) => {
      if (context?.oldData) {
        // if the context fails, revert the change with the oldData without new tag
        qc.setQueryData<InfiniteData<RecentCallRecords> | undefined>(
          [selectedLocationIds, ...queryKey],
          context.oldData
        );
        onFailure?.({ tag, op: 'add' });
      }
    },
    onSuccess: (_, tag) => {
      const updatedTagIds = getUpdatedTagIds();
      onSuccess?.({ tag, op: 'add', updatedTagIds });
    },
  });

  const { mutate: removeTag } = useMutation({
    mutationFn: async (tag: Tag) => {
      return removeCallRecordTags({
        channelId,
        tagIds: [tag.id],
        callRecordLocationId,
        startTime: callStartedAt,
      });
    },
    onMutate: async (tag: Tag) => {
      const oldData = qc.getQueryData<InfiniteData<RecentCallRecords> | undefined>([selectedLocationIds, ...queryKey]);
      qc.setQueryData<InfiniteData<RecentCallRecords> | undefined>([selectedLocationIds, ...queryKey], (oldData) => {
        return optimisticRemoveTag?.({ oldData, tag, channelId }) ?? oldData;
      });
      return { oldData };
    },
    onError: (_err, tag, context) => {
      if (context?.oldData) {
        qc.setQueryData<InfiniteData<RecentCallRecords> | undefined>(
          [selectedLocationIds, ...queryKey],
          context.oldData
        );
        onFailure?.({ tag, op: 'remove' });
      }
    },
    onSuccess: (_, tag) => {
      const updatedTagIds = getUpdatedTagIds();
      onSuccess?.({ tag, op: 'remove', updatedTagIds });
    },
  });

  return { applyTag, removeTag };
};
